import { useEffect, useState } from "react";
import { X } from "lucide-react";

const STORAGE_KEY = "zx_privacy_ack_v1";

export default function CookieBanner() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Delay slightly so it doesn't fight with hero animations
    const t = setTimeout(() => {
      try {
        if (!window.localStorage.getItem(STORAGE_KEY)) setOpen(true);
      } catch {
        setOpen(true);
      }
    }, 1200);
    return () => clearTimeout(t);
  }, []);

  const accept = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    } catch {
      /* ignore: private mode */
    }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-label="Privacy notice"
      className="fixed bottom-4 left-4 right-4 sm:left-6 sm:right-auto sm:bottom-6 sm:max-w-[420px] z-[60] animate-in fade-in slide-in-from-bottom-4 duration-500"
    >
      <div
        className="relative rounded-2xl border border-white/10 bg-[#0B0B0B]/95 backdrop-blur-xl shadow-[0_24px_60px_rgba(0,0,0,0.6)] p-5 sm:p-6"
        style={{
          boxShadow:
            "0 24px 60px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(217,249,157,0.06)",
        }}
      >
        {/* Premium accent line on top */}
        <div
          aria-hidden
          className="absolute -top-px left-6 right-12 h-px"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(217,249,157,0.55), transparent)",
          }}
        />

        <div className="flex items-start gap-3">
          <div className="flex-1">
            <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#D9F99D]/90">
              Privacy
            </div>
            <p className="mt-2 text-[13px] sm:text-sm text-neutral-200 leading-relaxed">
              We use only the data you submit through our contact form to
              respond to your enquiry. No advertising trackers — read our{" "}
              <a
                href="/privacy"
                data-testid="cookie-banner-privacy-link"
                className="text-[#D9F99D] border-b border-[#D9F99D]/40 hover:border-[#D9F99D] transition-colors"
              >
                Privacy Policy
              </a>
              .
            </p>
            <div className="mt-4 flex items-center gap-3">
              <button
                type="button"
                onClick={accept}
                data-testid="cookie-banner-accept"
                className="inline-flex items-center gap-2 rounded-full bg-[#D9F99D] hover:bg-[#E4FBB1] text-black text-[13px] font-semibold px-4 py-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D9F99D] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0B]"
              >
                Got it
              </button>
              <a
                href="/privacy"
                className="text-[12px] font-mono uppercase tracking-[0.18em] text-neutral-400 hover:text-white transition-colors"
              >
                Learn more
              </a>
            </div>
          </div>
          <button
            type="button"
            aria-label="Dismiss privacy notice"
            data-testid="cookie-banner-close"
            onClick={accept}
            className="text-neutral-500 hover:text-white p-1 -m-1 rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D9F99D]/60"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}
